/**
 * @packageDocumentation
 * @module f1/adt
 */

import { t_collection } from '../types';
import is_array from '../is/is_array';
import is_object from '../is/is_object';

/**
 * @enum
 * - __into__ When merging interleave arrays
 * - __onto__ When merging append arrays
 * - __over__ When merging clobber arrays
 */
type t_merge = 'into' | 'onto' | 'over';
type t_merge_fn = (k: string, a: t_collection, b: t_collection) => t_collection | undefined;

/** @ignore */
const mergeRule: Record<t_merge, t_merge_fn> = {
  into: (k, a, b) => {
    debugger;
    return a === undefined ? b : b === undefined ? a : undefined;
  },
  onto: (k, a, b) => {
    return is_array(a) && is_array(b) ? a.concat(b) : undefined;
  },
  over: (k, a, b) => {
    if (is_array(a) && is_array(b)) {
      a.length = b.length;
      return b;
    }

    return undefined;
  }
};

/** @ignore */
function _merge<D, O>(fn: t_merge_fn, destination: D, overwrite: O): D & O {
  const acc = destination;
  let isFromComplex;
  let isToComplex;
  let from;
  let to;
  let k;
  for (k in overwrite) {
    from = overwrite[k];
    to = acc[k];
    isFromComplex = is_object(from) || is_array(from);
    isToComplex = is_object(to) || is_array(to);

    if (isFromComplex && isToComplex) {
      _merge(fn, acc[k], fn(k, acc[k], from) || from);
    } else if (from === undefined) {
      acc[k] = to;
    } else {
      acc[k] = from;
    }
  }

  return acc as D & O;
}

export interface Merge {
  /**
   * Deep merge on object/array over another, against a merge rule to handle arrays
   *
   * @public
   * @function f1.merge
   * @param rule select a rule to merge by for handling arrays in the tree
   * @param destination the destination object to merge over
   * @param overwrite the merge object
   * @example
   *
   * ```ts
   * merge('into', { x: [1, 2] }, { x: [2] }) -> { x: [2, 2] }
   * merge('onto', { x: [1] }, { x: [2] }) -> { x: [1, 2] }
   * merge('over', { x: [1] }, { x: [2] }) -> { x: [2] }
   * ```
   */
  <RULE extends t_merge, D, O, R>(rule: RULE, destination: D, overwrite: O): R;
  /**
   * {@link FnMergeValue}
   * @param value
   */
  <RULE extends t_merge, D, O, R>(rule: RULE, destination: D): (overwrite: O) => R;
}

/**
 * @see Merge
 */
export default <D, O>(rule: t_merge, destination: D, overwrite: O): D & O =>
  _merge(mergeRule[rule], destination, overwrite);
